// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Text channel for intake. Text turns go through the backend (NOT Vapi — the raw Vapi web
// SDK always acquires the mic), so a text patient never sees a mic prompt. The turn streams
// back over SSE and we normalize it to the SAME IntakeMessage the voice path uses. Same
// conversationId, same ai-gateway (snapshot + red-flag), one shared history.
//
// Wire shape (per openapi.yaml): request body is { content }; the response streams UNNAMED
// `data:` frames (MessageChunk deltas — accumulate `content`) and ends with a NAMED
// `event: done` frame carrying StreamTerminal.

import { apiFetch, ApiError } from '@/lib/api'
import type { MessageChunk, ParsedSseFrame, StreamTerminal, TextTurnResult } from '../types'

export interface TextTurnHandlers {
  /** One streaming delta chunk. Group by (role, sequenceNumber): same sequenceNumber
   *  → append to the same bubble; a new sequenceNumber → a new message bubble. */
  onDelta: (chunk: MessageChunk) => void
  /** Terminal event (`event: done`): authoritative status + flags for this turn. */
  onTerminal: (result: TextTurnResult) => void
  onError: (error: Error) => void
}

/**
 * Send one text turn and stream the assistant reply. Returns a cancel function that aborts
 * the in-flight stream (e.g. on unmount).
 */
export function sendTextTurn(
  conversationId: string,
  text: string,
  handlers: TextTurnHandlers,
): () => void {
  const controller = new AbortController()

  void (async () => {
    try {
      const res = await apiFetch(`/v1/intake/${conversationId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ content: text }),
        signal: controller.signal,
      })
      if (!res.ok || !res.body) {
        throw new ApiError(res.status, 'intake_message_failed', `Intake message failed: ${res.status}`)
      }
      await consumeSse(res.body, handlers)
    } catch (err) {
      if (controller.signal.aborted) return
      handlers.onError(err instanceof Error ? err : new Error(String(err)))
    }
  })()

  return () => controller.abort()
}

/**
 * Parse one raw SSE frame. Returns a streaming delta chunk, the terminal `done` event, or
 * null for keep-alives (":" comments), data-less frames, or malformed JSON. The frame is a
 * delta unless it carries `event: done`. Pure — unit-tested.
 */
export function parseSseFrame(rawFrame: string): ParsedSseFrame | null {
  let eventName: string | null = null
  const dataLines: string[] = []
  for (const line of rawFrame.split('\n')) {
    if (line.startsWith(':')) continue // keep-alive comment
    if (line.startsWith('event:')) eventName = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (dataLines.length === 0) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null

  if (eventName === 'done') {
    return { kind: 'done', terminal: parsed as StreamTerminal }
  }

  // Unnamed data frame → streaming delta chunk; require a string content.
  const chunk = parsed as MessageChunk
  if (typeof chunk.content !== 'string') return null
  return { kind: 'chunk', chunk }
}

/** Read the SSE body: frames are separated by a blank line; dispatch each. */
async function consumeSse(body: ReadableStream<Uint8Array>, handlers: TextTurnHandlers): Promise<void> {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sep: number
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const rawFrame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      dispatchSseFrame(rawFrame, handlers)
    }
  }
  if (buffer.trim()) dispatchSseFrame(buffer, handlers)
}

function dispatchSseFrame(rawFrame: string, handlers: TextTurnHandlers): void {
  const frame = parseSseFrame(rawFrame)
  if (!frame) return

  if (frame.kind === 'chunk') {
    handlers.onDelta(frame.chunk)
    return
  }
  handlers.onTerminal({
    status: frame.terminal.status,
    emergencyFlagged: frame.terminal.emergencyFlagged === true,
    prepNoteReady: frame.terminal.prepNoteReady === true,
  })
}
